import { styled } from "@mui/system";
import InputLabel from "@mui/material/InputLabel";
import { brandFontFamily, brandFontSize, brandInputTextColor } from "../../../utils/variables";

const CustomInputLabel = styled(InputLabel)(({ fullHeight, hasLabel }) => ({
  color: brandInputTextColor,
  fontSize: brandFontSize,
  fontWeight: 400,
  fontFamily: `${brandFontFamily} !important`,
  marginBottom: fullHeight && hasLabel ? "6px" : "",
  display: "flex",
  // A uniform row height keeps every label the same height whether or not it
  // carries a chip or counter, so side-by-side fields stay on the same line —
  // and the slack below the baseline keeps taller content off the input.
  minHeight: hasLabel ? "26px" : undefined,
  // Baseline, not center: the marker renders at a smaller size than the label,
  // and centering it leaves its text riding above its baseline.
  alignItems: "baseline",
  // Icons and other element children still read better optically centred.
  "& > *:not(span)": {
    alignSelf: "center",
  },
}));


/**
 * Single source of truth for how a field advertises whether it is required.
 *
 * `requirement` is the prop to reach for on new work:
 *   "required" -> red asterisk
 *   "optional" -> italic "– Optional"
 *   "none"     -> no marker
 *
 * BaseTextField and BaseAutoSelect each shipped their own legacy prop
 * (`optional` and `required`) with opposite defaults, so they map those onto
 * `requirement` themselves when it isn't passed. Once every call site sets
 * `requirement` the legacy props can go.
 */
const FieldLabel = ({
  label,
  requirement = "none",
  hint,
  htmlFor,
  fullHeight = true,
  endContent,
  children,
  ...props
}) => (
  <CustomInputLabel
    shrink={false}
    htmlFor={htmlFor}
    fullHeight={fullHeight}
    hasLabel={!!label}
    {...props}
  >
    {label}
    {requirement === "required" && (
      <span className="text-red-500 ml-0.5" aria-hidden="true">
        *
      </span>
    )}
    {requirement === "optional" && <span className="font-light italic ml-1">– Optional</span>}
    {hint && <span className="text-xs ml-2 font-light">{hint}</span>}
    {children}
    {/* Pushed to the far end of the label row — used for counters and the like,
        so they don't cost an extra row beneath the input. */}
    {endContent && <span className="ml-auto pl-2 font-light shrink-0">{endContent}</span>}
  </CustomInputLabel>
);

export default FieldLabel;
